import React, { useEffect, useState } from "react";
import { BsFillPeopleFill } from "react-icons/bs";
import { IconContext } from "react-icons";

import { MdThumbUp } from "react-icons/md";
import { HiExclamationTriangle } from "react-icons/hi2";
import { useSelector } from "react-redux";
import { selectFeedback, selectRoom, selectUserData } from "../../features/store";
import moment from "moment/moment";
import PopUp from "../../components/PopUp";
import { FaTimesCircle } from "react-icons/fa";

import { PieChart, Pie, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, Legend } from "recharts";
import { useSnackbar } from "notistack";
import Explanation from "../../components/Explanation";
import { getAvgFeedback, getComplaint } from "../../apis/Feedback.Api";
import { getCumulativeCount } from "../../apis/PeopleCount.Api";

const colors = ["#574477", "#b34a80", "#f7625a", "#ffa600"];
const SummaryRow = ({ currBuilding }) => {
    const rooms = useSelector(selectRoom);
    const userData = useSelector(selectUserData);
    const feedbackDict = useSelector(selectFeedback);
    const [mostUsed, setMostUsed] = useState("--");
    const [highestRating, setHighestRating] = useState(["--", "--"]);
    const [complaints, setComplaints] = useState(null);
    const [complaintCount, setComplaintCount] = useState("--");
    const [openInspect, setOpenInspect] = useState(false);
    const [displayData, setDisplayData] = useState([]);
    const [displayPie, setDisplayPie] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (!openInspect) {
            initSummary();
        }
    }, [openInspect, currBuilding, rooms, feedbackDict]);

    // make charts
    useEffect(() => {
        if (!complaints) return;
        let temp = {};
        let pieData = [
            { name: "Wet Floor", value: 0 },
            { name: "Ammonia level High", value: 0 },
            { name: "No Paper Roll", value: 0 },
            { name: "No Hand Soap", value: 0 },
        ];
        const converter = {
            "Wet Floor": 0,
            "Ammonia level High": 1,
            "No Paper Roll": 2,
            "No Hand Soap": 3,
        };
        for (let key of Object.keys(complaints)) {
            let complaint = complaints[key];
            let room = rooms[complaint.room_id];
            if (complaint.message === "") continue;
            if (room.roomName in temp) {
                temp[room.roomName][complaint.message] += 1;
                temp[room.roomName]["sum"] += 1;
                pieData[converter[complaint.message]].value += 1;
            } else {
                temp[room.roomName] = {
                    name: room.roomName,
                    "Wet Floor": 0,
                    "Ammonia level High": 0,
                    "No Paper Roll": 0,
                    "No Hand Soap": 0,
                    sum: 0,
                };
                temp[room.roomName][complaint.message] += 1;
                temp[room.roomName]["sum"] += 1;
                pieData[converter[complaint.message]].value += 1;
            }
        }
        let arr = Object.keys(temp);
        arr.sort((a, b) => temp[b].sum - temp[a].sum);
        let copy = [];
        for (let i of arr) {
            copy.push(temp[i]);
        }
        setDisplayData(copy);

        setDisplayPie(pieData);
    }, [complaints]);

    async function initSummary() {
        const now = new Date();
        let sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        let format = moment(sevenDaysAgo).format("YYMMDDhhmmssSSS");
        // get most visited room
        // let cumulativeCounts = await getRoomsCumulativeCount(token);
        let cumulativeCounts = await getCumulativeCount({
            room_id: null,
            start_time: format,
        });

        if (!cumulativeCounts.data.indicator) {
            enqueueSnackbar(cumulativeCounts.data.message, { variant: "error" });
            return;
        }
        cumulativeCounts = cumulativeCounts.data.message;
        let filtered = cumulativeCounts.filter((element) => {
            let rid = element.room_id;
            return rooms[rid]?.buildingId === currBuilding.buildingId;
        });
        if (filtered.length !== 0) {
            const max = filtered.reduce(function (prev, current) {
                return prev.cumulative_count > current.cumulative_count ? prev : current;
            });
            setMostUsed(max.room_name);
        } else {
            setMostUsed("--");
        }

        // Get room with highest rating
        let feedback = await getAvgFeedback({ start_time: format });

        if (!feedback.data.indicator) {
            enqueueSnackbar(feedback.data.message, { variant: "error" });
            return;
        }

        feedback = feedback.data.message;
        let filteredFeedback = feedback.filter((element) => {
            let rid = element.room_id;

            return rooms[rid]?.buildingId === currBuilding.buildingId;
        });

        if (filteredFeedback.length !== 0) {
            const highest = filteredFeedback.reduce(function (prev, current) {
                return prev.count > current.count ? prev : current;
            });
            setHighestRating([highest.room_name, highest.count]);
        } else {
            setHighestRating("--");
        }

        // Get recent complaints
        let complaintList = await getComplaint({ start_time: format });
        if (!complaintList.data.indicator) {
            enqueueSnackbar(complaintList.data.message, { variant: "error" });
            return;
        }

        complaintList = complaintList.data.message;

        let filteredComplaint = complaintList.filter((element) => {
            let rid = element.room_id;
            return rooms[rid]?.buildingId === currBuilding.buildingId;
        });
        if (filteredComplaint.length !== 0) {
            setComplaints(filteredComplaint);
        } else {
            setComplaints([]);
        }
        setComplaintCount(filteredComplaint.length);
    }

    return (
        <>
            <div className="summary-wrapper">
                <div className="summary-header">Summary</div>
                <div className="summary-row">
                    <SummaryItem
                        title={"Most Usage Toilet"}
                        data={mostUsed}
                        icon={<BsFillPeopleFill />}
                        explanation={"Toilet with the most visitors for the past 7 days."}
                    />
                    <SummaryItem
                        title={"Highest Rating Toilet"}
                        data={`${highestRating[0]} | ${highestRating[1]} people`}
                        icon={<MdThumbUp />}
                        explanation={"Toilet with the highest average rating for the past 7 days."}
                    />
                    <SummaryItem
                        title={"Recent Complaints"}
                        data={`${complaintCount} complaints | `}
                        icon={<HiExclamationTriangle />}
                        inspect={complaintCount !== 0}
                        setOpenInspect={setOpenInspect}
                        explanation={"Complaints information of this building."}
                    />
                </div>
            </div>

            <PopUp width={60} height={85} switchOn={openInspect}>
                <div className="pop-up-content-wrapper">
                    <div className="pop-up-header">
                        <div className="pop-up-row">
                            <h4>Complaints</h4>
                            <FaTimesCircle
                                className="pop-up-close-btn"
                                onClick={() => {
                                    setOpenInspect(false);
                                }}
                            />
                        </div>
                        <div className="pop-up-division-line"></div>
                    </div>
                    <div className="pop-up-body">
                        <div className="pop-up-content-margin inspect-complaint">
                            <h4> Complaints Past 7 Days</h4>

                            <ComplaintBarChart data={displayData} xKey="name" />
                            <br />
                            <h4>All Complaints </h4>

                            <ComplaintPieChart data={displayPie} />
                        </div>
                    </div>
                </div>
            </PopUp>
        </>
    );
};

const SummaryItem = ({ title, data, icon, inspect, setOpenInspect, explanation }) => {
    return (
        <div className="summary-item">
            <div className="icon">
                <IconContext.Provider value={{ size: "100%" }}>{icon}</IconContext.Provider>
            </div>
            <div>
                <div className="sum-title-row">
                    <p className="sum-title">{title}</p>
                    <Explanation explanation={explanation} />
                </div>

                <div className="sum-data">
                    {data}
                    {inspect && (
                        <span
                            className="inspect"
                            onClick={() => {
                                setOpenInspect(true);
                            }}
                        >
                            inspect
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const YAxisLeftTick = ({ y, payload: { value } }) => {
    return (
        <g transform={`translate(${0},${y})`}>
            <text x={0} y={0} textAnchor="start" alignmentBaseline="middle" fill="#666" fontSize={"12px"}>
                {value.length > 15 ? value.slice(0, 15) + "..." : value}
            </text>
        </g>
    );
};

const ComplaintBarChart = ({ data, xKey }) => {
    const keys = ["Wet Floor", "Ammonia level High", "No Paper Roll", "No Hand Soap"];
    return (
        <div className="complaint-bar-chart-wrapper">
            <ResponsiveContainer width={"95%"} height={120 * data.length}>
                <BarChart data={data} layout="vertical" margin={{ left: 0 }} isAnimationActive>
                    <XAxis type="number" />
                    <YAxis
                        yAxisId={0}
                        dataKey={xKey}
                        height={20}
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        tick={YAxisLeftTick}
                        width={120}
                    />
                    <Legend />
                    <Tooltip />

                    <Bar dataKey={keys[0]} barSize={18} stackId={"a"} fill={colors[0]} />
                    <Bar dataKey={keys[1]} barSize={18} stackId={"a"} fill={colors[1]} />
                    <Bar dataKey={keys[2]} barSize={18} stackId={"a"} fill={colors[2]} />
                    <Bar dataKey={keys[3]} barSize={18} stackId={"a"} fill={colors[3]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const ComplaintPieChart = ({ data }) => {
    return (
        <div className="complaint-pie-chart-wrapper">
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        dataKey="value"
                        isAnimationActive={true}
                        data={data}
                        cx="50%"
                        cy="45%"
                        outerRadius={"90%"}
                        fill="#8884d8"
                        //label
                    >
                        {[0, 1, 2, 3].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                        ))}
                    </Pie>
                    <Legend />
                    <Tooltip />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default SummaryRow;
